import React, { useState, useEffect } from 'react';

interface Advertisement {
  id: number;
  title: string;
  type: string;
  price: string | null;
  category: string;
  description: string;
  contact_phone: string;
  province: string;
  status: string;
  user_name?: string;
  created_at: string;
}

const Advertisements: React.FC = () => {
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadAds = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://fellfell.ir/api/advertisements.php', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 آگهی‌ها دریافت شد:', data);
      
      if (data.success && Array.isArray(data.data)) {
        setAds(data.data);
      } else {
        setError('خطا در دریافت آگهی‌ها: ساختار داده نامعتبر');
      }
    } catch (err: any) {
      console.error('❌ خطا:', err);
      setError('خطا در ارتباط با سرور: ' + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAds();
  }, []);

  const handleStatusChange = async (id: number, status: string) => {
    try {
      const response = await fetch(`https://fellfell.ir/api/advertisements.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.success) {
        showToast(`وضعیت آگهی به ${status === 'approved' ? 'تایید شده' : status === 'rejected' ? 'رد شده' : 'در انتظار'} تغییر یافت`, 'success');
        loadAds();
      } else {
        showToast('خطا در تغییر وضعیت: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('خطا در تغییر وضعیت: ' + err.message, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('آیا از حذف این آگهی مطمئن هستید؟')) return;
    try {
      const response = await fetch(`https://fellfell.ir/api/advertisements.php?id=${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        showToast('آگهی با موفقیت حذف شد', 'success');
        loadAds();
      } else {
        showToast('خطا در حذف آگهی: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('خطا در حذف آگهی: ' + err.message, 'error');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; bg: string }> = {
      'approved': { label: '✅ تایید شده', color: '#2e7d32', bg: '#e8f5e9' },
      'pending': { label: '⏳ در انتظار', color: '#f57c00', bg: '#fff3e0' },
      'rejected': { label: '❌ رد شده', color: '#c62828', bg: '#ffebee' },
      'expired': { label: '⏰ منقضی', color: '#757575', bg: '#f5f5f5' }
    };
    return statusMap[status] || { label: status, color: '#333', bg: '#f5f5f5' };
  };

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'sell': '🟢 فروش',
      'buy': '🔵 خرید',
      'request': '🟡 درخواست',
      'cooperation': '🟣 همکاری'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div style={{ direction: 'rtl', textAlign: 'center', padding: '50px' }}>
        ⏳ در حال بارگذاری آگهی‌ها...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ direction: 'rtl', padding: '20px' }}>
        <div style={{ background: '#ffebee', color: '#c62828', padding: '20px', borderRadius: '12px' }}>
          ❌ {error}
          <button 
            onClick={loadAds}
            style={{ marginRight: '15px', padding: '5px 15px', background: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', padding: '20px' }}>
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 28px',
          borderRadius: '12px',
          fontSize: '15px',
          zIndex: 9999,
          background: toast.type === 'success' ? '#2e7d32' : '#c62828',
          color: '#fff',
          boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#1e3a1e' }}>📢 مدیریت آگهی‌ها</h1>
        <span style={{ color: '#888' }}>{ads.length} آگهی</span>
      </div>

      {ads.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '12px' }}>
          <p style={{ fontSize: '18px', color: '#888' }}>📭 هیچ آگهی‌ای ثبت نشده است</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: '#f5faf5' }}>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>عنوان</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>نوع</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>قیمت</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>کاربر</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>وضعیت</th>
                <th style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '2px solid #e8f0e8' }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((ad) => {
                const status = getStatusBadge(ad.status || 'pending');
                return (
                  <tr key={ad.id} style={{ borderBottom: '1px solid #eef5ee' }}>
                    <td style={{ padding: '12px 15px', fontWeight: '500' }}>{ad.title}</td>
                    <td style={{ padding: '12px 15px' }}>{getTypeLabel(ad.type)}</td>
                    <td style={{ padding: '12px 15px', color: '#2e7d32' }}>
                      {ad.price ? new Intl.NumberFormat('fa-IR').format(Number(ad.price)) + ' تومان' : 'توافقی'}
                    </td>
                    <td style={{ padding: '12px 15px' }}>{ad.user_name || 'کاربر'}</td>
                    <td style={{ padding: '12px 15px' }}>
                      <select
                        value={ad.status || 'pending'}
                        onChange={(e) => handleStatusChange(ad.id, e.target.value)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          background: status.bg,
                          color: status.color,
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="pending">⏳ در انتظار</option>
                        <option value="approved">✅ تایید</option>
                        <option value="rejected">❌ رد</option>
                        <option value="expired">⏰ منقضی</option>
                      </select>
                    </td>
                    <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleDelete(ad.id)}
                        style={{ padding: '6px 12px', background: '#ffebee', color: '#c62828', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Advertisements;